import React from 'react'
import { Tooltip } from '@mui/material'
import { ILoanerRecord, IMinimalDevice } from '../../LoanerVehicles'
import { ReactComponent as EditSvg } from '../../../../assets/svg/edit.svg'
import { ReactComponent as DeleteIcon } from '../../../../assets/svg/delete.svg'
import { ReactComponent as CheckSvg } from '../../../../assets/svg/check_24px.svg'
import LoanerVehicleSelector from '../LoanerVehicleSelector'
import CustomDatePicker from '../CustomDatePicker'
import styles from '../../LoanerVehicles.module.scss'

interface IActiveLoanerRowProps {
  loaner: ILoanerRecord
  onSave: (loanerId: string) => void
  onEdit: (loanerId: string) => void
  onDelete: (loanerId: string) => void
  onFieldChange: (
    loanerId: string,
    field: keyof ILoanerRecord,
    value: string
  ) => void
  onLoanerVehicleSelect: (loanerId: string, device: IMinimalDevice) => void
  availableDevices: IMinimalDevice[]
}

const ActiveLoanerRow = ({
  loaner,
  onSave,
  onEdit,
  onDelete,
  onFieldChange,
  onLoanerVehicleSelect,
  availableDevices,
}: IActiveLoanerRowProps) => {
  return (
    <tr key={loaner.id}>
      {/* Assigned Vehicle */}
      <td className={styles.readonlyField}>
        {loaner.assignedVehicle.name}
      </td>

      {/* Loaner Vehicle */}
      <td>
        <LoanerVehicleSelector
          availableDevices={availableDevices}
          selectedDevice={loaner.loanerVehicle}
          onSelect={(device) => onLoanerVehicleSelect(loaner.id, device)}
          isEditing={loaner.isEditing}
        />
      </td>

      {/* Issued date */}
      <td>
        {loaner.isEditing ? (
          <CustomDatePicker
            value={loaner.issuedDate}
            onChange={(date) => onFieldChange(loaner.id, 'issuedDate', date)}
          />
        ) : (
          <span className={styles.readonlyText}>
            {loaner.issuedDate || ' '}
          </span>
        )}
      </td>

      {/* Estimated return date */}
      <td>
        {loaner.isEditing ? (
          <CustomDatePicker
            value={loaner.estimatedReturnDate}
            onChange={(date) => onFieldChange(loaner.id, 'estimatedReturnDate', date)}
          />
        ) : (
          <span className={styles.readonlyText}>
            {loaner.estimatedReturnDate || ' '}
          </span>
        )}
      </td>

      {/* Assigned staff */}
      <td>
        {loaner.isEditing ? (
          <input
            type="text"
            className={styles.textInput}
            value={loaner.assignedStaff}
            onChange={(e) => onFieldChange(loaner.id, 'assignedStaff', e.target.value)}
            placeholder="Enter name..."
          />
        ) : (
          <Tooltip title={loaner.assignedStaff} placement="top">
            <span className={styles.readonlyText}>
              {loaner.assignedStaff || ' '}
            </span>
          </Tooltip>
        )}
      </td>

      {/* Note */}
      <td>
        {loaner.isEditing ? (
          <input
            type="text"
            className={styles.textInput}
            value={loaner.note}
            onChange={(e) => onFieldChange(loaner.id, 'note', e.target.value)}
            placeholder="Add note..."
          />
        ) : (
          <Tooltip
            title={
              <div style={{ maxWidth: 100, whiteSpace: 'normal' }}>
                {loaner.note}
              </div>
            }
            placement="top-start"
          >
            <span className={styles.readonlyText}>
              {loaner.note || ' '}
            </span>
          </Tooltip>
        )}
      </td>

      {/* Closed date */}
      <td>
        {loaner.isEditing ? (
          <CustomDatePicker
            value={loaner.closedDate}
            onChange={(date) => onFieldChange(loaner.id, 'closedDate', date)}
            hint='After clicking "Select", this loaner will be moved to "Closed".'
          />
        ) : (
          <span className={styles.readonlyText}>
            {loaner.closedDate || ' '}
          </span>
        )}
      </td>

      {/* Action */}
      <td>
        <div className={styles.actionButtons}>
          {/* Save / Edit */}
          {loaner.isEditing ? (
            <Tooltip
              title="Save"
              placement="top"
              key={`edit-${loaner.id}-${loaner.isEditing}`}
            >
              <button
                className={styles.saveButton}
                onClick={() => onSave(loaner.id)}
              >
                <CheckSvg />
              </button>
            </Tooltip>
          ) : (
            <Tooltip
              title="Edit"
              placement="top"
              key={`edit-${loaner.id}-${loaner.isEditing}`}
            >
              <button
                className={styles.editButton}
                onClick={() => onEdit(loaner.id)}
              >
                <EditSvg />
              </button>
            </Tooltip>
          )}

          {/* Delete */}
          <Tooltip title="Delete" placement="top">
            <button
              className={styles.deleteButton}
              onClick={() => onDelete(loaner.id)}
            >
              <DeleteIcon />
            </button>
          </Tooltip>
        </div>
      </td>
    </tr>
  )
}

export default ActiveLoanerRow